import { Link } from "react-router-dom";
import { ArrowUpRight, Wheat } from "lucide-react";
import { useState } from "react";
import { LOGOS, SITE, BRANDS } from "@/data/site";
import { toast } from "sonner";

const TRUST = [
  { t: "100% Millet", s: "Wholesome & Natural" },
  { t: "No Maida", s: "Only Real Ingredients" },
  { t: "Rich in Nutrition", s: "Protein · Fibre · Minerals" },
  { t: "Trusted Quality", s: "Safe & Delicious" },
  { t: "Sustainable", s: "Better for You & Planet" },
];

// Official UN SDG identity — number, title, description, and our locally-stored official icon.
const SDGS = [
  { n: 2,  title: "Zero Hunger",                          icon: "/images/sdg/sdg-2.jpg",  desc: "Promoting nutritious, millet-based meals that support healthier communities." },
  { n: 3,  title: "Good Health & Well-being",             icon: "/images/sdg/sdg-3.jpg",  desc: "Nutritious food choices for a healthier and happier life." },
  { n: 8,  title: "Decent Work & Economic Growth",        icon: "/images/sdg/sdg-8.jpg",  desc: "Fair livelihoods for farmers, partners and families across the ecosystem." },
  { n: 12, title: "Responsible Consumption & Production", icon: "/images/sdg/sdg-12.jpg", desc: "Mindful sourcing, minimal waste, and sustainable practices." },
];

export default function Footer() {
  const [email, setEmail] = useState("");

  const onSubscribe = (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email.");
      return;
    }
    toast.success("You're on the list — millet stories coming your way.");
    setEmail("");
  };

  return (
    <footer data-testid="site-footer">
      {/* Trust strip */}
      <div className="trust-strip">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 py-7 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6">
          {TRUST.map((x) => (
            <div key={x.t} className="flex items-start gap-3">
              <Wheat size={22} strokeWidth={1.5} className="text-[var(--gold)] mt-1" />
              <div>
                <div className="font-serif-display text-base font-semibold text-[var(--ink)]">{x.t}</div>
                <div className="text-xs text-[var(--ink-3)] mt-1">{x.s}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main footer */}
      <div className="bg-[var(--green-dark)] text-white">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 pt-16 pb-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <Link to="/" className="flex items-center gap-3">
                <img src={LOGOS.native_touch} alt="Native Touch" className="h-14 w-14 rounded-full bg-[#fdf8e6] p-0.5 object-contain" />
                <div>
                  <div className="font-serif-display text-2xl">Native Touch</div>
                  <div className="kbd text-[var(--gold-soft)] mt-0.5">{SITE.parent}</div>
                </div>
              </Link>
              <p className="font-serif-display text-2xl md:text-3xl mt-7 leading-snug">
                Four brands. <br />
                One <span className="text-[var(--gold)]">food ecosystem.</span>
              </p>

              <form onSubmit={onSubscribe} data-testid="footer-newsletter-form" className="mt-8 flex border border-white/30 rounded-md overflow-hidden bg-white/5">
                <input
                  data-testid="footer-newsletter-email"
                  type="email"
                  placeholder="Your email · join the millet movement"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent px-4 py-3 text-sm placeholder:text-white/55 focus:outline-none"
                />
                <button
                  type="submit"
                  data-testid="footer-newsletter-submit"
                  className="bg-[var(--gold)] text-[var(--green-deep)] px-5 text-[0.72rem] uppercase tracking-[0.22em] font-bold hover:brightness-95 transition"
                >
                  Subscribe
                </button>
              </form>
            </div>

            <div className="md:col-span-2">
              <div className="overline text-[var(--gold-soft)]">Ecosystem</div>
              <ul className="mt-5 space-y-3 text-sm">
                {BRANDS.map((b) => (
                  <li key={b.slug}>
                    <Link to={`/brands/${b.slug}`} data-testid={`footer-brand-${b.slug}`} className="link-underline text-white/85">
                      {b.name.split("—")[0].trim()}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="overline text-[var(--gold-soft)]">Shop</div>
              <ul className="mt-5 space-y-3 text-sm text-white/85">
                <li><Link className="link-underline" to="/products" data-testid="footer-products">All Products</Link></li>
                <li><Link className="link-underline" to="/products" data-testid="footer-noodles">Millet Noodles</Link></li>
                <li><Link className="link-underline" to="/products" data-testid="footer-pasta">Pasta</Link></li>
                <li><Link className="link-underline" to="/products" data-testid="footer-snacks">Snacks</Link></li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="overline text-[var(--gold-soft)]">Company</div>
              <ul className="mt-5 space-y-3 text-sm text-white/85">
                <li><Link className="link-underline" to="/about" data-testid="footer-about">About</Link></li>
                <li><Link className="link-underline" to="/franchise" data-testid="footer-franchise">Franchise</Link></li>
                <li><Link className="link-underline" to="/locations" data-testid="footer-locations">Locations</Link></li>
                <li><Link className="link-underline" to="/contact" data-testid="footer-contact">Contact</Link></li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="overline text-[var(--gold-soft)]">Get in touch</div>
              <div className="mt-5 text-sm text-white/85 space-y-2 leading-relaxed">
                <p>{SITE.contact.address}</p>
                <p><a href={`mailto:${SITE.contact.email}`} data-testid="footer-email" className="link-underline">{SITE.contact.email}</a></p>
                <p>{SITE.contact.phone}</p>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {SITE.socials.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    data-testid={`footer-social-${s.label.toLowerCase().replace(/\s+|\(|\)/g, "")}`}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold border border-white/30 px-2.5 py-1.5 rounded-full hover:bg-[var(--gold)] hover:text-[var(--green-deep)] hover:border-[var(--gold)] transition"
                  >
                    {s.label} <ArrowUpRight size={11} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* ───────────── SDG ALIGNMENT — pledge layout ───────────── */}
          <section data-testid="footer-sdg" className="mt-10 pt-8 border-t border-white/15">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
              <div className="lg:col-span-4">
                <img
                  src="/images/sdg/sdg-logo.png"
                  alt="UN Sustainable Development Goals"
                  className="block w-full max-w-[280px] h-auto object-contain bg-white rounded-md px-3 py-2"
                />
              </div>
              <div className="lg:col-span-8">
                <div className="overline text-[var(--gold-soft)]">Our Pledge</div>
                <p className="mt-2.5 text-sm md:text-[14.5px] text-white/85 leading-[1.55] max-w-xl">
                  We proudly stand with the United Nations{" "}
                  <strong className="text-white font-semibold">Sustainable Development Goals</strong>{" "}
                  — fuelling a healthier, fairer, and more sustainable tomorrow with every millet bowl we serve.
                </p>
              </div>
            </div>

            <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5 md:gap-3">
              {SDGS.map((g) => (
                <li
                  key={g.n}
                  data-testid={`footer-sdg-${g.n}`}
                  className="flex items-center gap-3 p-2.5 md:p-3 rounded-md border border-white/12 bg-white/[0.03] hover:border-[var(--gold)]/40 hover:bg-white/[0.05] transition"
                >
                  <img
                    src={g.icon}
                    alt={`SDG ${g.n} · ${g.title}`}
                    loading="lazy"
                    className="w-12 h-12 md:w-[52px] md:h-[52px] rounded object-cover shrink-0"
                  />
                  <p className="text-[11.5px] md:text-[12px] text-white/80 leading-snug">{g.desc}</p>
                </li>
              ))}
            </ul>
          </section>

          <div className="mt-12 pt-7 border-t border-white/15 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/55">
            <p>© {new Date().getFullYear()} Native Touch Group · An EBG Group Company.</p>
            <p className="flex flex-wrap gap-x-4 gap-y-1 md:justify-end items-center">
              <span>Privacy</span><span className="opacity-50">·</span>
              <span>Terms</span><span className="opacity-50">·</span>
              <span>Made in India</span><span className="opacity-50">·</span>
              <span>
                Design & Development by{" "}
                <a
                  href="https://www.reachvel.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-credit-reachvel"
                  className="text-[var(--gold-soft)] hover:text-[var(--gold)] underline underline-offset-2 font-semibold"
                >
                  Reachvel
                </a>
              </span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
